import React from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { CourseGroupV2, CourseV2 } from '../api/models/Course';
import { Student } from '../api/models/Student';
import { images } from '../assets/images';
import { colors, fonts, scale } from '../Styles';
import { getCourseColor } from '../util/CourseColor';
import { MNOHSCourseTile } from './MNOHSCourseTile';
import { MNOHSFloatingIndicator } from './MNOHSFloatingIndicator';

type Props = {
  /** Current page of course list - controlled state */
  currentPage: number;
  /** Name of the selected course */
  courseName: string;
  /** Loading state of course groups request */
  isGettingCourseGroups: boolean;
  /** List of courses */
  courseList: CourseV2[];
  /** The current course group */
  courseGroup: CourseGroupV2;
  /** student */
  student: Student | undefined;
  /** If user clicks the pagination dot to turn the page */
  userClickedPaginationDot: (currentPage: number) => void;
};

export const MNOHSCourseDetails: React.FC<Props> = ({
  currentPage,
  courseName,
  isGettingCourseGroups,
  courseList,
  courseGroup,
  student,
  userClickedPaginationDot,
}) => {
  const startIndex = currentPage * 7;
  const creditsDone = student?.credits ? student.credits.toFixed(2) : 0;
  const creditsOutOf = student?.creditsOutOf ? student.creditsOutOf.toFixed(2) : 0;

  const _getPages = () => {
    const totalCourses = courseList.length;
    return Math.ceil(totalCourses / 7); // 7 items per page
  };

  const _previousPage = () => {
    if (currentPage > 0) {
      userClickedPaginationDot(currentPage - 1);
    }
  };

  const _nextPage = () => {
    const pages = _getPages();
    if (currentPage < pages - 1) {
      userClickedPaginationDot(currentPage + 1);
    }
  };

  return (
    <View>
      <Text style={styles.creditsText}>
        CREDITS DONE: {creditsDone}/{creditsOutOf}
      </Text>
      <Text style={[styles.courseTitle, { borderBottomColor: getCourseColor(courseGroup?.courseGroupCode) }]}>
        {courseName}
      </Text>

      <View style={styles.courseListContainer}>
        <View style={styles.courseListHeader}>
          <Text style={styles.courseFirstHeaderLabel}>STAT</Text>
          <Text style={styles.courseSecondHeaderLabel}>COURSE NAME</Text>
        </View>

        {isGettingCourseGroups ? (
          <MNOHSFloatingIndicator block={true} />
        ) : (
          <View style={styles.courseList}>
            {!courseList.length && <Text style={styles.emptyList}>No courses taken</Text>}
            {courseList.slice(startIndex, startIndex + 7).map((course, i) => {
              return (
                <View key={course.courseName + i} style={styles.courseItem}>
                  <View style={styles.courseItemImageContainer}>
                    <MNOHSCourseTile courseGroup={courseGroup} course={course} size={scale(30)} onPress={() => {}} />
                  </View>
                  <Text style={styles.courseItemText}>{course.courseName}</Text>
                </View>
              );
            })}
          </View>
        )}

        {courseList.length >= 7 && (
          <View style={styles.dots}>
            <TouchableOpacity onPress={_previousPage}>
              <Image source={images.chevronLeft} style={styles.caret} resizeMode="contain" />
            </TouchableOpacity>
            {[...new Array(_getPages())].map((page, i) => (
              <TouchableOpacity key={`page-key${page}${i}`} onPress={() => userClickedPaginationDot(i)}>
                <View style={i === currentPage ? styles.activeDot : styles.dot} />
              </TouchableOpacity>
            ))}
            <TouchableOpacity onPress={_nextPage}>
              <Image style={styles.caret} source={images.chevronRight} resizeMode="contain" />
            </TouchableOpacity>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  creditsText: {
    ...fonts.descriptionText,
    fontWeight: 'bold',
    alignSelf: 'flex-end',
  },
  courseTitle: {
    ...fonts.header2,
    marginTop: 16,
    textTransform: 'uppercase',
    alignSelf: 'center',
    borderBottomWidth: 4,
    borderBottomColor: colors.grayDark,
  },
  courseListContainer: {
    marginTop: 24,
  },
  courseListHeader: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  courseFirstHeaderLabel: {
    ...fonts.linkedText,
    flex: 1,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  courseSecondHeaderLabel: {
    ...fonts.linkedText,
    textTransform: 'uppercase',
    flex: 5,
    marginLeft: 8,
  },
  courseList: {},
  courseItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 8,
  },
  courseItemImageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 4,
  },
  courseItemText: {
    ...fonts.header2,
    flex: 5,
    marginLeft: 8,
  },
  dots: {
    marginVertical: 16,
    flexDirection: 'row',
    alignSelf: 'center',
    alignItems: 'center',
  },
  activeDot: {
    marginHorizontal: 6,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: colors.black,
  },
  dot: {
    marginHorizontal: 6,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.grayDark,
  },
  emptyList: {
    ...fonts.paragraph,
    textAlign: 'center',
    marginTop: 8,
  },
  caret: {
    width: 40,
    height: 40,
  },
});
